import React from 'react';
import type { JDAnalysis as JDAnalysisType } from '../types';
import { MapPin, Briefcase, BarChart3, Clock } from 'lucide-react';

interface JDAnalysisProps {
  analysis: JDAnalysisType;
  keywordMatches?: string[];
}

const JDAnalysis: React.FC<JDAnalysisProps> = ({ analysis, keywordMatches = [] }) => {
  const isSkillMatched = (skill: string) => {
    return keywordMatches.some(
      (kw) => kw.toLowerCase().includes(skill.toLowerCase()) ||
              skill.toLowerCase().includes(kw.toLowerCase())
    );
  };

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h2 className="text-xl font-semibold text-foreground m-0 mb-2">
          {analysis.jobTitle}
          {analysis.company && <span className="text-secondary-foreground font-normal"> at {analysis.company}</span>}
        </h2>
        <div className="flex flex-wrap gap-4">
          {analysis.location && (
            <span className="text-sm text-secondary-foreground inline-flex items-center gap-1">
              <MapPin className="w-3.5 h-3.5" /> {analysis.location}
            </span>
          )}
          {analysis.employmentType && (
            <span className="text-sm text-secondary-foreground inline-flex items-center gap-1">
              <Briefcase className="w-3.5 h-3.5" /> {analysis.employmentType}
            </span>
          )}
          {analysis.experienceLevel && (
            <span className="text-sm text-secondary-foreground inline-flex items-center gap-1">
              <BarChart3 className="w-3.5 h-3.5" /> {analysis.experienceLevel}
            </span>
          )}
          {analysis.yearsOfExperience && (
            <span className="text-sm text-secondary-foreground inline-flex items-center gap-1">
              <Clock className="w-3.5 h-3.5" /> {analysis.yearsOfExperience}
            </span>
          )}
        </div>
      </div>

      {analysis.summary && (
        <p className="text-sm leading-relaxed text-secondary-foreground m-0 p-4 bg-subtle rounded-lg border-l-3 border-l-brand-500">{analysis.summary}</p>
      )}

      <div className="flex flex-col gap-5">
        <div>
          <h3 className="text-xs font-semibold uppercase tracking-wider text-secondary-foreground m-0 mb-2">Required Skills</h3>
          <div className="flex flex-wrap gap-2">
            {analysis.requiredSkills.map((skill) => (
              <span key={skill} className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium ${isSkillMatched(skill) ? 'bg-emerald-50 border border-emerald-200 text-emerald-600' : 'bg-red-50 border border-red-200 text-red-500'}`}>
                {isSkillMatched(skill) ? '✅' : '❌'} {skill}
              </span>
            ))}
          </div>
        </div>

        {analysis.niceToHaveSkills && analysis.niceToHaveSkills.length > 0 && (
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wider text-secondary-foreground m-0 mb-2">Nice to Have</h3>
            <div className="flex flex-wrap gap-2">
              {analysis.niceToHaveSkills.map((skill) => (
                <span key={skill} className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium ${isSkillMatched(skill) ? 'bg-emerald-50 border border-emerald-200 text-emerald-600' : 'bg-red-50 border border-red-200 text-red-500'}`}>
                  {isSkillMatched(skill) ? '✅' : '❌'} {skill}
                </span>
              ))}
            </div>
          </div>
        )}

        <div>
          <h3 className="text-xs font-semibold uppercase tracking-wider text-secondary-foreground m-0 mb-2">Key Responsibilities</h3>
          <ul className="list-none p-0 m-0 flex flex-col gap-2">
            {analysis.keyResponsibilities.map((r, i) => (
              <li key={i} className="text-sm text-secondary-foreground p-2 px-3 bg-subtle rounded-btn leading-relaxed">{r}</li>
            ))}
          </ul>
        </div>

        {analysis.qualifications && analysis.qualifications.length > 0 && (
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wider text-secondary-foreground m-0 mb-2">Qualifications</h3>
            <ul className="list-none p-0 m-0 flex flex-col gap-2">
              {analysis.qualifications.map((q, i) => (
                <li key={i} className="text-sm text-secondary-foreground p-2 px-3 bg-subtle rounded-btn leading-relaxed">{q}</li>
              ))}
            </ul>
          </div>
        )}

        {analysis.companyCulture && (
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wider text-secondary-foreground m-0 mb-2">Company Culture</h3>
            <p className="text-sm leading-relaxed text-secondary-foreground m-0">{analysis.companyCulture}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default JDAnalysis;
